import {
  ApplicationRef,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';

import { metaReducers, STORE_EFFECTS } from './app/store';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { tagUnsupportedBrowserEvent } from '@app/core/unsupported-browser';
import { initCssColorVars } from './css-vars';
import { APP_BASE_HREF } from '@angular/common';
import { AppBase } from '@app/shared/models/app-base';
import { INTERCEPTORS_PROVIDERS } from './app/core/interceptors/interceptors.provieders';
import { DateFnsLocaleService } from '@app/core/settings/language/date-fns-locale.service';
import { MobileAppIntegrationService } from './app/mobile-app-integration/mobile-app-integration.service';
import { SettingsService } from '@app/core/settings/settings.service';
import { LegalDocumentLinksService } from './app/core/legal-documents/legal-document-links.service';
import { bootstrapApplication } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { StoreModule } from '@ngrx/store';
import { reducers } from './app/core/store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { CoreModule } from './app/core/core.module';
import { AppRoutingModule } from './app/app-routing.module';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { AppComponent } from './app/app.component';

initCssColorVars();

const DMP_API_EVENTS_URL_REGEXP = /^https:\/\/.*\.member\.(placewise\.com|dev\.placewise\.com)\/api\/v2\/.*\/(events)$/;
if (environment.sentryDSN) {
  Sentry.init({
    dsn: environment.sentryDSN,
    release: environment.commitId,
    environment: environment.sentryEnvironment,
    integrations: [
      //https://placewisegroup.slack.com/archives/C013HJCGZ33/p1745389780942909?thread_ts=1744950589.907239&cid=C013HJCGZ33
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [422],
        failedRequestTargets: [DMP_API_EVENTS_URL_REGEXP],
      }),
    ],
    sendDefaultPii: true,
    beforeSend: (event) => tagUnsupportedBrowserEvent(event),
  });
}

if (environment.production) {
  enableProdMode();
}

const bootstrap = (): Promise<ApplicationRef> => bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(
      ServiceWorkerModule.register(`${window.location.origin}/ngsw-worker.js`, {
        enabled: environment.registerServiceWorker,
        registrationStrategy: 'registerWhenStable:1000',
      }),
      StoreModule.forRoot(reducers, { metaReducers }),
      EffectsModule.forRoot(STORE_EFFECTS),
      CoreModule,
      AppRoutingModule,
      LoadingBarModule,
      SettingsService,
    ),
    {
      provide: APP_BASE_HREF,
      useValue: `${AppBase.href}/`,
    },
    ...INTERCEPTORS_PROVIDERS,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ logErrors: true }),
    },
    provideAppInitializer((): void => {
      const service = inject(DateFnsLocaleService);
      service.init();
    }),
    provideAppInitializer((): void => {
      const service = inject(MobileAppIntegrationService);
      service.init();
    }),
    LegalDocumentLinksService,
  ],
});

bootstrap().catch(err => console.log(err));
